import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Router } from '@angular/router';
import { TaxonomyApiService } from '../../../../services/api-services/taxonomy-api.service';
import { TaxonomyService } from '../../../../services/taxonomy.service';
import { NotificationService } from '../../../../services/notification.service';
import { TaxonomyButtonComponent } from '../../taxonomy-button/taxonomy-button.component';

@Component({
  selector: 'app-general-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatOptionModule,
    TaxonomyButtonComponent,
  ],
  templateUrl: './general-settings.component.html',
  styleUrls: ['./general-settings.component.scss'],
})
export class GeneralSettingsComponent implements OnInit {
  private http = inject(HttpClient);

  taxonomyId: string;
  taxonomy: any = { name: '', description: '', queueGroups: [] };
  originalTaxonomy = { name: '', description: '', queueGroups: [] as string[] };

  queueGroups = new FormControl<string[]>([]);
  searchText: string = '';
  deleteInput: string = '';

  queueGroupsList: QueueGroup[] = [];
  selectedGroups: string[] = [];

  filteredQueueGroups: {
    selected: QueueGroup[];
    unselected: QueueGroup[];
  } = { selected: [], unselected: [] };

  showConfirmationPopup = false;

  constructor(
    private taxonomyApiService: TaxonomyApiService,
    public taxonomyService: TaxonomyService,
    private router: Router,
    public notificationService: NotificationService
  ) {
    this.taxonomyId = JSON.parse(sessionStorage.getItem('selectedTaxonomy') || '{}').taxonomy_id || '';
  }

  ngOnInit() {
    this.getTaxonomyDetails();
  }

  getTaxonomyDetails() {
    const body = { taxonomy_id: this.taxonomyId };
    this.taxonomyApiService.getSpecificTaxonomyV2ById(body).subscribe({
      next: (res) => {
        const data = res.data;
        this.taxonomy = {
          name: data.TaxonomyName,
          description: data.TaxonomyDescription || '',
          queueGroups: data.LinkedQueueGroups,
        };
        this.selectedGroups = data.LinkedQueueGroups || [];
        this.queueGroups.setValue(this.selectedGroups);
        this.originalTaxonomy = {
          name: data.TaxonomyName || '',
          description: data.TaxonomyDescription || '',
          queueGroups: [...(data.LinkedQueueGroups || [])],
        };
        this.getListOfQueueGroups();
      },
      error: (err) => console.error('Failed to fetch taxonomy', err),
    });
  }

  getListOfQueueGroups() {
    this.taxonomyApiService.getListOfQueueGroups({}).subscribe({
      next: (res) => {
        const combinedList: string[] = res?.data?.flatMap((groupObj: any) => [
          ...(groupObj.agent_to_agent_queues_en || []),
          ...(groupObj.agent_to_customer_queues_en || []),
        ]) || [];

        this.queueGroupsList = combinedList.map((group: string) => ({
          name: group,
          selected: this.selectedGroups.includes(group),
        }));

        this.filterQueueGroups();
      },
      error: (err) => console.error('Failed to fetch queue groups', err),
    });
  }

  filterQueueGroups() {
    const search = this.searchText.toLowerCase();
    this.filteredQueueGroups.selected = this.queueGroupsList.filter(
      (g) => g.selected && g.name.toLowerCase().includes(search)
    );
    this.filteredQueueGroups.unselected = this.queueGroupsList.filter(
      (g) => !g.selected && g.name.toLowerCase().includes(search)
    );
  }

  onDropdownToggle(opened: boolean) {
    if (!opened) {
      this.searchText = '';
    }
    this.filterQueueGroups();
  }

  onGroupSelectChange(group: QueueGroup, event: Event) {
    event.stopPropagation();
    group.selected = !group.selected;
    this.updateSelections();
  }

  deselectOption(groupName: string) {
    const group = this.queueGroupsList.find((g) => g.name === groupName);
    if (group) group.selected = false;
    this.updateSelections();
  }

  private updateSelections() {
    this.selectedGroups = this.queueGroupsList.filter((g) => g.selected).map((g) => g.name);
    this.queueGroups.setValue(this.selectedGroups);
    this.filterQueueGroups();
  }

  confirmSaveChanges() {
    const payload = {
      taxonomy_name: this.taxonomy.name,
      taxonomy_id: this.taxonomyId,
      taxonomy_description: this.taxonomy.description || '',
      linked_queue_groups: this.queueGroups.value ?? [],
    };

    this.taxonomyApiService.updateTaxonomyV2(payload).subscribe({
      next: () => {
        this.notificationService.success('update taxonomy', 'Taxonomy Updated');

        // Fetch the updated taxonomy data with complete version history
        this.taxonomyApiService.getVersionHistoryById({ taxonomy_id: this.taxonomyId }).subscribe({
          next: (response) => {
            const version_history_details = response.data.version_history_details || [];
            const selected_taxonomy_version = version_history_details[0] || null;

            const updatedTaxonomy = {
              taxonomy_id: this.taxonomyId,
              taxonomy_name: this.taxonomy.name,
              version_history_details: version_history_details,
              selected_taxonomy_version: selected_taxonomy_version
            };
            this.taxonomyService.setSelectedValue(updatedTaxonomy);
            this.router.navigate(['/taxonomy-dashboard', this.taxonomyId]);
          },
          error: (err) => {
            console.error('Failed to fetch updated taxonomy', err);
            this.router.navigate(['/taxonomy-dashboard', this.taxonomyId]);
          }
        });
      },
      error: (err) => {
        console.error('Failed to update taxonomy', err);
        this.notificationService.error('update taxonomy', 'Taxonomy Updation Failed');
      },
    });
    this.showConfirmationPopup = false;
  }

  cancelSave() {
    this.showConfirmationPopup = false;
    this.router.navigate(['/taxonomy-dashboard', this.taxonomyId]);
  }

  handleSaveChanges() {
    this.showConfirmationPopup = true;
  }

  handleCancel() {
    // Restore the taxonomy object from the originally fetched values
    this.taxonomy.name = this.originalTaxonomy.name;
    this.taxonomy.description = this.originalTaxonomy.description;
    this.taxonomy.queueGroups = [...this.originalTaxonomy.queueGroups];

    // Reset the selected groups
    this.selectedGroups = [...this.originalTaxonomy.queueGroups];
    this.queueGroups.setValue([...this.originalTaxonomy.queueGroups]);

    // Reset selection in the list of queue groups
    this.queueGroupsList.forEach(group => {
      group.selected = this.originalTaxonomy.queueGroups.includes(group.name);
    });

    // Reapply filtering
    this.filterQueueGroups();
  }

  handleDeleteTaxonomy(deleteInput: string) {
    if (deleteInput === this.taxonomy.name) {
      this.taxonomyApiService.deleteTaxonomyV2({ taxonomy_id: this.taxonomyId }).subscribe({
        next: () => {
          this.notificationService.success('delete taxonomy', 'Taxonomy Deleted');
          this.taxonomyService.removeTaxonomyFromStorage(this.taxonomyId);
          this.router.navigate(['/taxonomy-dashboard']);
        },
        error: (err) => {
          console.error('Failed to delete taxonomy', err);
          this.notificationService.error('delete taxonomy', 'Deletion Failed');
        },
      });
    } else {
      this.notificationService.error('delete taxonomy', 'Taxonomy name mismatch');
    }
  }

  isSaveEnabled(): boolean {
    const nameTrimmed = this.taxonomy.name?.trim();
    const descTrimmed = this.taxonomy.description?.trim() ?? '';
    const groupChanged =
      JSON.stringify(this.originalTaxonomy.queueGroups.sort()) !== JSON.stringify(this.selectedGroups.sort());
    return (
      !!nameTrimmed &&
      (nameTrimmed !== this.originalTaxonomy.name || descTrimmed !== this.originalTaxonomy.description || groupChanged)
    );
  }

  isDeleteEnabled(): boolean {
    return this.deleteInput.trim() === this.originalTaxonomy.name;
  }
}

interface QueueGroup {
  name: string;
  selected: boolean;
}
